"use client";

import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthProvider } from './AuthContext';
import { initializeEnhancedLocalStorage } from '@/lib/utils/localStorage';
import { initializeDeploymentCleanup } from '@/lib/utils/deploymentInit';

import { logDevelopmentConfig } from '@/lib/config/development';

// =============================================================================
// APP CONTEXT INTERFACE
// =============================================================================

interface AppContextValue {
  isInitialized: boolean;
  developmentMode: boolean;
  version: string;
}

// =============================================================================
// APP CONTEXT
// =============================================================================

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// =============================================================================
// APP PROVIDER COMPONENT
// =============================================================================

interface AppProviderProps {
  children: React.ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;

    const initializeApp = async () => {
      try {
        // Log development configuration
        logDevelopmentConfig();

        // Initialize deployment cleanup (handles version-based localStorage cleanup)
        initializeDeploymentCleanup({
          enableLogging: process.env.NODE_ENV === 'development',
          preserveUserSession: true,
          preserveSettings: true,
          onCleanupComplete: (stats) => {
            if (process.env.NODE_ENV === 'development' && stats.total > 0) {
              console.log('🧹 [DEV] Deployment cleanup completed:', stats);
            }
          }
        });

        // Initialize enhanced localStorage with development configuration
        initializeEnhancedLocalStorage();

        // Mark as initialized
        setIsInitialized(true);

        if (process.env.NODE_ENV === 'development') {
          console.log('🚀 [DEV] App initialization complete');
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
        // Still mark as initialized to prevent blocking the app
        setIsInitialized(true);
      }
    };

    initializeApp();
  }, [hasMounted]);

  const contextValue: AppContextValue = {
    isInitialized,
    developmentMode: process.env.NODE_ENV === 'development',
    version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
  };

  return (
    <AppContext.Provider value={contextValue}>
      <AuthProvider>
        {children}
      </AuthProvider>
    </AppContext.Provider>
  );
} 
